import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { callApi, formatDate, formatMoney, getCdnUrl, getInlineLoader } from "../Helpers";

export default function PaymentRecordOptions({ uuid }) {

  const appContext = useContext(AppContext);

  const [ready, setReady] = useState(false);
  const [data, setData] = useState(null);
  const [checking, setChecking] = useState(false);

  async function init() {
    setReady(false);
    await getData();
    setReady(true);
  }

  function getData() {
    return new Promise(async resolve => {
      await callApi("get-payment-data", {
        paymentUuid: uuid,
      }).then((response) => {
        if (response.status === 1) {
          setData(response.data);
        } else {
          appContext.tellError(response.msg);
        }

        resolve(response);
      })
    })
  }

  function recheckPayment() {
    return new Promise(async resolve => {
      setChecking(true);

      await callApi("recheck-mobile-push-payment", {
        paymentUuid: uuid,
      }).then((response) => {
        if (response.status === 1) {
          setData(response.data);
          appContext.tellMessage("Payment status refreshed");
        } else {
          appContext.tellError(response.msg);
        }

        setChecking(false);
        resolve(response);
      })
    })
  }

  function printReceipt() {
    window.print();
  }

  useEffect(() => {
    init();
  }, [uuid])

  useEffect(() => {
    appContext.setShowOverlayLoader(checking);

    return () => {
      appContext.setShowOverlayLoader(false);
    }
  }, [checking])

  if (ready) {
    if (!data) {
      return (
        <div className="container py-4">
          Payment record unavailable.
        </div>
      )
    }

    const isSuccessful = data.status === "successful";
    const canRecheck = (
      data.paymentMethod === "mobile_push" &&
      ["processing", "submitted", "failed"].includes(data.status)
    );

    return (
      <div
        className="container"
        style={{
          paddingTop: "20px",
          paddingBottom: "80px",
        }}
      >
        <h5 className="font-bold">
          Payment details
        </h5>

        <div className="card mb-3">
          <div className="card-body">
            <h4 className="font-bold text-primary">
              TZS {formatMoney(data.amount)}
            </h4>

            <span className={`badge ${isSuccessful ? "bg-success" : "bg-dark"}`}>
              {data.status}
            </span>

            <hr />

            <p>
              <span className="text-muted">Service:</span>
              <br />
              {data.titleEn}
            </p>

            <p>
              <span className="text-muted">Inquiry:</span>
              <br />
              {data.orderTitle}
            </p>

            <p>
              <span className="text-muted">Method:</span>
              <br />
              {String(data.paymentMethod).replaceAll("_", " ")}
            </p>

            <p>
              <span className="text-muted">Submitted:</span>
              <br />
              {formatDate(data.dateCreated)}
            </p>

            {
              data.externalReference
                ? <p>
                  <span className="text-muted">Gateway reference:</span>
                  <br />
                  {data.externalReference}
                </p>
                : ""
            }

            {
              data.failureReason
                ? <div className="alert alert-danger">
                  {data.failureReason}
                </div>
                : ""
            }

            {
              data.evidenceText
                ? <div className="alert alert-light">
                  <b>Submitted evidence</b>
                  <br />
                  {data.evidenceText}
                </div>
                : ""
            }

            {
              (data.attachments || []).map((file) => (
                <a
                  className="btn btn-sm btn-outline-primary me-2 mb-2"
                  href={getCdnUrl(file.fileUrl)}
                  target="_blank"
                  rel="noreferrer"
                  key={file.uuid}
                >
                  {file.originalName || file.attachmentType}
                </a>
              ))
            }
          </div>
        </div>

        {
          canRecheck
            ? <button
              className="btn btn-primary mb-3"
              disabled={checking || !data.externalReference}
              onClick={recheckPayment}
            >
              {checking ? "Checking..." : "Check payment status"}
            </button>
            : ""
        }

        {
          isSuccessful
            ? <div className="card mb-3" id="payment-receipt">
              <div className="card-body">
                <h6 className="font-bold">
                  Kamola LandLink Payment Receipt
                </h6>

                <p className="mb-1">
                  <b>Receipt:</b> {data.receiptNumber}
                </p>

                <p className="mb-1">
                  <b>Amount:</b> TZS {formatMoney(data.amount)}
                </p>

                <p className="mb-1">
                  <b>Service:</b> {data.titleEn}
                </p>

                <p className="mb-1">
                  <b>Confirmed:</b>{" "}
                  {
                    data.confirmedAt
                      ? formatDate(data.confirmedAt, false)
                      : "N/A"
                  }
                </p>

                <p className="mb-3">
                  <b>Confirmation:</b>{" "}
                  {
                    data.confirmationSource === "snippe"
                      ? "Snippe (automatic)"
                      : "Administrator"
                  }
                </p>

                <button
                  className="btn btn-sm btn-outline-dark"
                  onClick={printReceipt}
                >
                  Print receipt
                </button>
              </div>
            </div>
            : ""
        }

        <h6 className="font-semi-bold">
          Activity
        </h6>

        {
          (data.events || []).map((event) => (
            <div
              className="border-start ps-3 pb-3"
              key={event.uuid}
            >
              <b>{String(event.eventType).replaceAll("_", " ")}</b>
              <br />
              <small className="text-muted">
                {event.actorType} · {formatDate(event.dateCreated)}
              </small>
              {
                event.notes
                  ? <p className="mb-0">{event.notes}</p>
                  : ""
              }
            </div>
          ))
        }
      </div>
    )
  } else {
    return (
      <div style={{ padding: "40px" }}>
        {getInlineLoader()}
      </div>
    )
  }
}
